
import { connect } from 'react-redux';
import { AGENT_TYPE, USER_ROLE } from '../../../../../shared/constants';
import request from '../../../request';
import { hideLoading, showLoading } from '../../../loading';
import swal from 'sweetalert';
import ResponsiveTable from "../../../components/ResponsiveTable";
import Gender from '../../../components/Gender';
import Editor from "./Editor";
import PillLabel from '../../../components/PillLabel';
import { IconButton } from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon, Visibility } from '@mui/icons-material';
import { requestConfirmation } from '../../../utils';
import { getAgentFullName, hasIntersection } from "../../../../../shared/utils";
import DateTime from "../../../components/DateTime";
import Email from "../../../components/Email";
import Phone from "../../../components/Phone";
import { Link } from "react-router";
import links from "../../../links";
import NavigationStateSaver from "../../../components/NavigationStateSaver";


class _List extends NavigationStateSaver {

   state = {
      agents: null,
      page: 0,
      total: 0,
      limit: 10,
      type: '',
      typeIsFixed: false,
      editorOpen: false,
      agentBeingEdited: null
   }

   openEditor = (agentBeingEdited=null) => {
      this.setState({ agentBeingEdited, editorOpen: true })
   }

   closeEditor = (agent) => {
      const update = { agentBeingEdited: null, editorOpen: false }

      if (agent) {
         if (this.state.agentBeingEdited) {
            const agents = this.state.agents.map(a => a._id === agent._id ? agent : a);
            update.agents = agents;
         } else {
            update.agents = [ agent, ...this.state.agents ];
            update.total = this.state.total + 1;
         }
      }

      this.setState(update);
   }

   fetchAgents = async () => {

      try {

         showLoading();

         const { type, page, limit } = this.state
         const params = {
            type: type ? type : undefined,
            page: page + 1,
            limit
         }

         const res = await request.get('/api/general/agents', { params });
         const { total, agents } = res.data;

         this.setState({ total, agents });

      } catch (err) {
         swal("Error", err.toString(), "error");
      } finally {
         hideLoading();
      }
   }

   deleteAgent = async (agent) => {

      // confirm
      const question = `Are you sure you want to delete agent "${agent.name} ${agent.surname}"?`
      const confirm = await requestConfirmation(question);

      if (!confirm)
         return;

      try {

         showLoading();

         await request.delete(`/api/general/agents/${agent._id}`);
         const agents = this.state.agents.filter(a => a._id !== agent._id);
         this.setState({ agents });

      } catch (err) {
         swal("Error", err.toString(), "error");
      } finally {
         hideLoading();
      }
   }

   async componentDidMount() {

      await super.componentDidMount();

      const { roles } = this.props;

      const isTechnicalManager = roles.includes(USER_ROLE.TECHNICAL_MANAGER);
      const isSalesManager = roles.includes(USER_ROLE.SALES_MANAGER);
      
      if (isSalesManager || isTechnicalManager) {
         if (isSalesManager && isTechnicalManager) {
            // nothing to do
         } else {
            const typeIsFixed = true;
            const type = isSalesManager ? AGENT_TYPE.SALES : AGENT_TYPE.TECHNICAL;
            await this.updateState({ type, typeIsFixed });
         }
      }

      if (!this.state.agents)
         this.fetchAgents();

   }

   render() {

      const { roles } = this.props;
      const canAddOrUpdate = hasIntersection(roles, [USER_ROLE.SUPER_ADMIN, USER_ROLE.SALES_MANAGER, USER_ROLE.TECHNICAL_MANAGER]);

      let onAdd;
      if (canAddOrUpdate)
         onAdd = () => this.openEditor();

      return <>
         <ResponsiveTable
            cols={[
               {
                  field: 'name',
                  caption: 'Name',
                  render: (_, agent) => getAgentFullName(agent),
               },
               ...(this.state.typeIsFixed ? [] : [
                  {
                     field: 'type',
                     caption: 'Type',
                     render: type => <PillLabel className="uppercase">{type}</PillLabel>
                  }
               ]),
               {
                  field: 'gender',
                  caption: 'Gender',
                  render: gender => gender ? <Gender gender={gender} /> : ''
               },
               {
                  field: 'email', 
                  caption: 'Email',
                  render: email => email ? <Email email={email} /> : ''
               },
               { 
                  field: 'phone', 
                  caption: 'Phone',
                  render: phone => phone ? <Phone phone={phone} /> : ''
               },
               { field: 'address', caption: 'Address' },
               {
                  field: 'dob',
                  caption: 'DOB',
                  render: dob => dob ? <DateTime value={dob} dateOnly /> : ''
               },
               { field: 'national_id', caption: 'National ID' },
               {
                  field: 'actions',
                  caption: '',
                  render: (_, agent) => <>
                     <IconButton
                        onClick={() => this.openEditor(agent)}
                        size="small"
                        disabled={!canAddOrUpdate}
                     >
                        <EditIcon fontSize="small" />
                     </IconButton>
                     <IconButton
                        onClick={() => this.deleteAgent(agent)}
                        size="small"
                        color="error"
                        disabled={!canAddOrUpdate}
                     >
                        <DeleteIcon fontSize="small" />
                     </IconButton>
                     <Link to={links.agent(agent._id)}>
                        <IconButton size="small">
                           <Visibility />
                        </IconButton>
                     </Link>
                  </>
               }
            ]}
            rows={this.state.agents}
            fullHeight
            onRefresh={this.fetchAgents}
            onAdd={onAdd}
            pagination={{
               page: this.state.page,
               rowsPerPage: this.state.limit,
               count: this.state.total,
               labelRowsPerPage: "Agents per page",
               onPageChange: async (e, page) => {
                  await this.updateState({ page });
                  this.fetchAgents();
               },
               onRowsPerPageChange: async (e) => {
                  const limit = e.target.value;
                  await this.update({ limit, page: 0 });
                  this.fetchAgents();
               }
            }}
         />
         {
            this.state.editorOpen && <Editor
               agentBeingEdited={this.state.agentBeingEdited}
               onClose={this.closeEditor}
               type={this.state.type}
            />
         }
      </>
   }
}

const mapStateToProps = state => ({ roles: state.user.roles });
const List = connect(mapStateToProps)(_List);

export default List;